<script lang="ts">
  // Graphique en lignes SVG sobre (maquette du 21/10/2026) : grille horizontale, axe des valeurs à gauche,
  // étiquettes d'abscisse choisies par xTick, légende seulement s'il y a plusieurs séries. Infobulle native
  // (title) sur chaque point.
  export interface Series { name: string; color: string; values: (number | null)[]; dash?: boolean; dots?: boolean; w?: number }
  let { labels, series, yFmt, h = 260, xTick = () => true, xLabel = (l: string | number) => String(l), tipLabel }:
    { labels: (string | number)[]; series: Series[]; yFmt: (v: number) => string; h?: number;
      xTick?: (l: string | number, i: number) => boolean; xLabel?: (l: string | number, i: number) => string;
      tipLabel?: (l: string | number, i: number) => string } = $props();

  const W = 900, LM = 62, RM = 16, TM = 12, BM = 26;
  function niceStep(range: number) {
    const raw = range / 5 || 1, p = Math.pow(10, Math.floor(Math.log10(raw))), m = raw / p;
    return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
  }
  const geo = $derived.by(() => {
    const all = series.flatMap((s) => s.values.filter((v): v is number => v != null && Number.isFinite(v)));
    const min = Math.min(0, ...all);
    let max = all.length ? Math.max(...all) : 1;
    const step = niceStep(max - min);
    max = Math.ceil(max / step) * step || step;
    const n = labels.length;
    const x = (i: number) => LM + (n <= 1 ? 0 : (i * (W - LM - RM)) / (n - 1));
    const y = (v: number) => TM + (h - TM - BM) * (1 - (v - min) / (max - min));
    const ticks: number[] = [];
    for (let v = min; v <= max + 1e-9; v += step) ticks.push(v);
    const paths = series.map((s) => {
      let d = '', on = false;
      s.values.forEach((v, i) => {
        if (v == null || !Number.isFinite(v)) { on = false; return; }
        d += (on ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1) + ' ';
        on = true;
      });
      return d;
    });
    return { x, y, ticks, paths };
  });
  const tip = (i: number) => (tipLabel ? tipLabel(labels[i], i) : xLabel(labels[i], i) || String(labels[i]));
</script>

{#if series.length > 1}
  <div class="fr-legend">
    {#each series as s (s.name)}<span><i style="background:{s.color}"></i>{s.name}</span>{/each}
  </div>
{/if}
<svg viewBox="0 0 {W} {h}" width="100%" class="fr-chart" role="img">
  {#each geo.ticks as v (v)}
    <line x1={LM} x2={W - RM} y1={geo.y(v)} y2={geo.y(v)} stroke="#E8E4E2" />
    <text x={LM - 8} y={geo.y(v) + 3} text-anchor="end">{yFmt(v)}</text>
  {/each}
  {#each labels as l, i (i)}
    {#if xTick(l, i)}<text x={geo.x(i)} y={h - 8} text-anchor="middle">{xLabel(l, i)}</text>{/if}
  {/each}
  {#each series as s, k (s.name)}
    <path d={geo.paths[k]} fill="none" stroke={s.color} stroke-width={s.w || 2} stroke-dasharray={s.dash ? '4 4' : undefined} stroke-linejoin="round" />
    {#each s.values as v, i (i)}
      {#if v != null && Number.isFinite(v)}
        <circle cx={geo.x(i)} cy={geo.y(v)} r={s.dots ? 3 : 6} fill={s.dots ? s.color : 'transparent'}><title>{s.name} · {tip(i)} : {yFmt(v)}</title></circle>
      {/if}
    {/each}
  {/each}
</svg>
